// WHAT: Card component for displaying daily steps with streak and freeze status
// WHY:  Provides visual representation of step progress with motivational indicators
// DOMAIN: Health → Oura
// DEPENDENCIES: useOptimizedStepStreak, useOptimizedFreezeInfo hooks
// RISKS:
//   - NOTE: Progress calculation may not handle edge cases (negative values, division by zero)
//   - NOTE: Flame opacity calculation may not be intuitive
//   - NOTE: Warning threshold (18:00, 40% behind) is hardcoded - may not match user preferences

import { type FC } from "react";

import { Card } from "@/components/ui";

import { useOptimizedFreezeInfo } from "../../hooks/useOptimizedFreezeInfo";
import { useOptimizedStepStreak } from "../../hooks/useOptimizedStepStreak";

interface StreakDisplayProperties {
  error: null | string;
  loading: boolean;
  streakCount: number | undefined;
}

const StreakDisplay: FC<StreakDisplayProperties> = ({ error, loading, streakCount }) => {
  if (loading) {
    return <span className="text-xs text-neutral-500 font-medium">Loading…</span>;
  }
  if (error) {
    return <span className="text-xs text-red-400 font-medium">⚠︎ Error</span>;
  }
  if (streakCount !== undefined) {
    return <span className="text-xs text-neutral-500 font-medium">Streak: {streakCount}</span>;
  }
  return null;
};

interface FreezeBadgeProperties {
  active: boolean;
  error: null | string;
  loading: boolean;
  reason: null | string;
}

const FreezeBadge: FC<FreezeBadgeProperties> = ({ active, error, loading, reason }) => {
  if (loading) {
    return null;
  }
  if (error) {
    return <span className="text-xs text-red-400 font-medium">⚠︎</span>;
  }
  if (active) {
    return (
      <span className="text-xs font-medium text-yellow-400 flex items-center gap-1">
        ⏸️ Frozen{reason ? `: ${reason}` : ""}
      </span>
    );
  }
  return null;
};

/**
 * Props for StepsCard component
 */
interface StepsCardProperties {
  /** Daily step goal (default: 10,000) */
  goal?: number;
  /** Current step count */
  steps: number;
  /** Optional streak count override */
  streakCount?: number;
}

/**
 * WHAT:
 *   Card component that displays daily step progress with streak information,
 *   freeze status, and motivational indicators (flame, on-track status).
 *
 * WHY:
 *   Enables users to track their daily step progress with visual feedback
 *   and motivational elements.
 *
 * BEHAVIOR:
 *   - Loads streak and freeze information
 *   - Calculates expected steps based on time of day
 *   - Displays progress bars (actual vs expected)
 *   - Shows streak count and freeze status
 *   - Displays warning if streak is at risk
 *   - Updates flame opacity based on progress
 *
 * RISKS:
 *   - NOTE: Progress calculation may not handle edge cases (negative values, division by zero)
 *   - NOTE: Flame opacity calculation may not be intuitive
 *   - NOTE: Warning threshold (18:00, 40% behind) is hardcoded - may not match user preferences
 *
 * @param root0
 *
 * @param root0.steps
 *
 * @param root0.goal
 *
 * @param root0.streakCount
 *
 * @returns Steps card component
 *
 */
export const StepsCard: FC<StepsCardProperties> = ({
  goal = 10_000,
  steps,
  streakCount: streakCountProperty,
}) => {
  // NOTE: 1) Load streak data
  const {
    error: streakError,
    is_birthday_today,
    loading: streakLoading,
    streak_count,
  } = useOptimizedStepStreak();

  // NOTE: 2) Load freeze info
  const {
    error: freezeError,
    is_auto_freeze,
    is_manual_freeze,
    loading: freezeLoading,
    reason,
  } = useOptimizedFreezeInfo();

  // NOTE: 3) Derived states
  const streakCount: number | undefined =
    streakCountProperty ?? (streakLoading ? undefined : streak_count);

  const freezeActive = is_auto_freeze || is_manual_freeze;

  // NOTE: 4) Progress calculation
  const now = new Date();
  const hour = now.getHours();
  const minutesToday = hour * 60 + now.getMinutes();
  const dayProgress = minutesToday / 1440;

  const expectedSteps = Math.round(goal * dayProgress);
  const percentActual = Math.min((steps / goal) * 100, 100);
  const percentExpected = Math.min((expectedSteps / goal) * 100, 100);

  const onTrack = steps >= expectedSteps;
  const behindRatio = Math.max((expectedSteps - steps) / expectedSteps, 0);
  const flameOpacity = hour < 10 ? 1 : Math.max(0.3, 1 - behindRatio);
  const showWarning = !onTrack && hour >= 18 && behindRatio > 0.4;

  return (
    <Card className="flex flex-col justify-between h-full" padding="sm" variant="default">
      {/* Kopfzeile */}
      <div className="text-sm text-neutral-400 uppercase font-semibold mb-1 flex justify-between">
        <span>Steps</span>

        <div className="flex items-center gap-2">
          {/* Flamme */}
          <span className="transition-opacity duration-500" style={{ opacity: flameOpacity }}>
            🔥
          </span>

          {/* Streak */}
          <StreakDisplay error={streakError} loading={streakLoading} streakCount={streakCount} />

          {/* Freeze-Badge */}
          <FreezeBadge
            active={freezeActive}
            error={freezeError}
            loading={freezeLoading}
            reason={reason}
          />

          {/* Birthday-Badge */}
          {is_birthday_today ? (
            <span className="text-xs font-medium text-blue-400 flex items-center gap-1 bg-blue-900/20 px-2 py-1 rounded-full">
              🎂 Birthday - Streak continues!
            </span>
          ) : null}
        </div>
      </div>

      {/* Hauptwerte */}
      <div className="mb-3 text-center">
        <div className="text-3xl font-bold text-neutral-100">
          {steps.toLocaleString()}
          <span className="text-base font-normal text-neutral-500"> / {goal.toLocaleString()}</span>
        </div>

        <div className="text-xs text-neutral-500 mt-1">
          Expected by now: {expectedSteps.toLocaleString()}
        </div>

        <div
          className={`text-xs mt-1 font-medium ${onTrack ? "text-green-400" : "text-yellow-400"}`}
        >
          {onTrack ? "You're on track 💪" : "You're behind today ⏳"}
        </div>

        {showWarning && !freezeActive ? (
          <div className="text-xs text-red-400 mt-1 font-semibold">⚠️ Your streak is at risk</div>
        ) : null}
      </div>

      {/* Fortschritts-Balken */}
      <div className="relative h-4 rounded-full bg-neutral-700 overflow-hidden mt-2">
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-neutral-300 opacity-60"
          style={{ left: `${String(percentExpected)}%` }}
        />
        <div
          className="absolute inset-0 rounded-full bg-neutral-600"
          style={{ width: `${String(percentExpected)}%` }}
        />
        <div
          className={`absolute inset-0 rounded-full ${
            onTrack ? "bg-green-500" : "bg-yellow-500"
          } transition-all duration-700 ease-out`}
          style={{ width: `${String(percentActual)}%` }}
        />
      </div>
    </Card>
  );
};
